"use client";
import React, { useState } from "react";
import { Monitor, Tablet, Smartphone } from "lucide-react";
import { Template } from "@/types/models/template";
import { TemplatePageCard } from "./TemplatePageCard";

type ViewMode = "desktop" | "tablet" | "mobile";

interface PageSelection {
  pageId: string;
  selected: boolean;
  customData?: {
    title: string;
    slug: string;
    description: string;
    featured_image: string;
  };
}

interface TemplatePageSelectionGridProps {
  template: Template;
  pageSelections: PageSelection[];
  onTogglePageSelection: (pageId: string) => void;
}

export const TemplatePageSelectionGrid: React.FC<
  TemplatePageSelectionGridProps
> = ({ template, pageSelections, onTogglePageSelection }) => {
  const selectedPages = pageSelections.filter((s) => s.selected);
  const [viewMode, setViewMode] = useState<ViewMode>("desktop");

  return (
    <div
      className="bg-card dark:bg-auth-card rounded-xl border border-border dark:border-auth-background shadow-sm mb-8 animate-fadeInUp"
      style={{ animationDelay: "0.2s" }}
    >
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <h3 className="text-lg font-semibold text-foreground">
              Pages ({template.pages?.length || template.total_pages || 0})
            </h3>
            <div className="text-sm text-muted-foreground">
              {selectedPages.length} selected
            </div>
          </div>
          
          {/* View Mode Toggle */}
          <div className="flex items-center gap-1 bg-muted/50 rounded-lg p-1">
            <button
              onClick={() => setViewMode("desktop")}
              className={`p-2 rounded-md transition-colors ${
                viewMode === "desktop"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title="Desktop view"
            >
              <Monitor className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("tablet")}
              className={`p-2 rounded-md transition-colors ${
                viewMode === "tablet"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title="Tablet view"
            >
              <Tablet className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("mobile")}
              className={`p-2 rounded-md transition-colors ${
                viewMode === "mobile"
                  ? "bg-background shadow-sm text-foreground"
                  : "text-muted-foreground hover:text-foreground"
              }`}
              title="Mobile view"
            >
              <Smartphone className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {!template.pages || template.pages.length === 0 ? (
            <div className="col-span-full text-center py-12">
              <p className="text-muted-foreground">
                No pages found in this template.
              </p>
            </div>
          ) : (
            template.pages.map((page) => {
              // Safety check
              if (!page || !page.id) return null;
              
              const selection = pageSelections.find(
                (s) => s.pageId === page.id,
              );
              // Default to true since all pages are selected by default
              const isSelected = selection?.selected ?? true;

              return (
                <TemplatePageCard
                  key={page.id}
                  page={page}
                  isSelected={isSelected}
                  onToggleSelection={onTogglePageSelection}
                  viewMode={viewMode}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};
